import React, { useEffect, useState, useRef } from 'react';
import ProductCVGauges from '../components/ProductCVGauges';
import ProductNARGauges from '../components/ProductNARGauges';
import YieldGauges from '../components/YieldGauges';
import CMDensityChart from '../components/CMDensityChart';
import FilterCakeStats from '../components/FilterCakeStats';
import ConsumptionSummaryChart from '../components/ConsumptionSummaryChart';
import ProcessMassBalanceChart from '../components/ProcessMassBalanceChart';
import CyclonePressureChart from '../components/CyclonePressureChart';
import AccumulativeTonnesChart from '../components/AccumulativeTonnesChart';
import ComingSoon from '../components/ComingSoon';
import { CalendarIcon } from '@heroicons/react/solid';
import DateRangePicker from '../components/DateRangePicker';

function MetallurgicalAndKPI() {
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 6);
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const [endDate, setEndDate] = useState(() => {
    const d = new Date();
    d.setHours(23, 59, 59, 999);
    return d;
  });
  const [rangeLabel, setRangeLabel] = useState('');
  const [showPicker, setShowPicker] = useState(false);
  const defaultStart = useRef(startDate);
  const defaultEnd = useRef(endDate);
  const [showDefaultBtn, setShowDefaultBtn] = useState(false);
  const [fadeDefaultBtn, setFadeDefaultBtn] = useState(false);
  const [isCustom, setIsCustom] = useState(false);

  useEffect(() => {
    const f = d =>
      d.toLocaleString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      });
    setRangeLabel(`${f(startDate)} - ${f(endDate)}`);
  }, [startDate, endDate]);

  const prevWeek = () => {
    const step = endDate.getTime() - startDate.getTime() + 1000;
    setStartDate(new Date(startDate.getTime() - step));
    setEndDate(new Date(endDate.getTime() - step));
    setIsCustom(false);
  };
  const nextWeek = () => {
    const step = endDate.getTime() - startDate.getTime() + 1000;
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    const newEnd = new Date(endDate.getTime() + step);
    if (newEnd <= today) {
      setStartDate(new Date(startDate.getTime() + step));
      setEndDate(newEnd);
      setIsCustom(false);
    }
  };
  const todayEnd = new Date();
  todayEnd.setHours(23, 59, 59, 999);
  const disableNext = endDate.getTime() >= todayEnd.getTime();

  const toLocalIso = d => {
    const tzOffset = d.getTimezoneOffset() * 60000;
    return new Date(d.getTime() - tzOffset).toISOString().slice(0, 19);
  };

  const parsePickerValue = value => {
    if (!value) return null;
    const normalized = value.length === 16 ? `${value}:00` : value;
    const parsed = new Date(normalized);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const startStr = toLocalIso(startDate);
  const endStr = toLocalIso(endDate);

  const resetToDefault = () => {
    setStartDate(new Date(defaultStart.current));
    setEndDate(new Date(defaultEnd.current));
    setIsCustom(false);
  };

  useEffect(() => {
    const isDefault =
      startDate.getTime() === defaultStart.current.getTime() &&
      endDate.getTime() === defaultEnd.current.getTime();
    if (!isDefault) {
      setShowDefaultBtn(true);
    } else if (showDefaultBtn) {
      setFadeDefaultBtn(true);
      const t = setTimeout(() => {
        setShowDefaultBtn(false);
        setFadeDefaultBtn(false);
      }, 300);
      return () => clearTimeout(t);
    }
  }, [startDate, endDate, showDefaultBtn]);

  return (
    <div className="p-4 space-y-4 text-white flex h-full flex-col">
      <div className="flex items-center justify-center border-b border-gray-600 pb-2">
        <div className="flex items-center space-x-2">
          {showDefaultBtn && (
            <button
              onClick={resetToDefault}
              className={`px-2 py-1 bg-[#00D04C] rounded shadow hover:bg-[#00B240] transition ease-in-out duration-200 transition-opacity ${fadeDefaultBtn ? 'opacity-0' : 'opacity-100'}`}
            >
              Default
            </button>
          )}
          <button
            onClick={prevWeek}
            className="px-2 py-1 bg-[#0d2c3d] rounded shadow hover:bg-[#123b52] transition ease-in-out duration-200"
            aria-label="Previous Week"
          >{'<'}</button>
          <div
            className="px-3 py-1 bg-[#0d2c3d] rounded shadow flex items-center space-x-1 transition transform hover:scale-105 cursor-pointer"
            title="Week view – click arrows to navigate"
            onClick={() => setShowPicker(true)}
          >
            <CalendarIcon className="h-4 w-4" />
            <span className="text-sm">{rangeLabel}</span>
          </div>
          <button
            onClick={nextWeek}
            disabled={disableNext}
            className="px-2 py-1 bg-[#0d2c3d] rounded shadow hover:bg-[#123b52] disabled:opacity-50 transition ease-in-out duration-200"
            aria-label="Next Week"
          >{'>'}</button>
        </div>
      </div>

      {showPicker && (
        <DateRangePicker
          initialStart={toLocalIso(startDate)}
          initialEnd={toLocalIso(endDate)}
          onApply={(s, e) => {
            const start = parsePickerValue(s);
            const end = parsePickerValue(e);
            if (!start || !end) return;
            setStartDate(start);
            setEndDate(end);
            setIsCustom(true);
          }}
          onClose={() => setShowPicker(false)}
        />
      )}

      <div className="grid flex-1 min-h-0 overflow-auto grid-cols-4 grid-rows-4 gap-4">
        <div className="bg-[#0d2c3d] shadow-lg rounded-lg p-4 col-span-2 row-span-1 row-start-1 col-start-1 flex flex-col">
          <h2 className="text-center text-sm">Product Quality – CV (MJ/kg)</h2>
          <ProductCVGauges start={startStr} end={endStr} />
        </div>
        <div className="bg-[#0d2c3d] shadow-lg rounded-lg p-4 col-span-1 row-span-1 row-start-1 col-start-3 flex flex-col">
          <h2 className="text-center text-sm">SP level (tons)</h2>
          <AccumulativeTonnesChart start={startStr} end={endStr} />
        </div>
        <div className="bg-[#0d2c3d] shadow-lg rounded-lg p-4 col-span-1 row-span-1 row-start-1 col-start-4 flex flex-col">
          <h2 className="text-center text-sm">Fines KPIs</h2>
          <FilterCakeStats start={startStr} end={endStr} />
        </div>
        <div className="bg-[#0d2c3d] shadow-lg rounded-lg p-4 col-span-2 row-span-1 row-start-2 col-start-1 flex flex-col">
          <h2 className="text-center text-sm">Product Quality – NAR (Kcal/kg)</h2>
          <ProductNARGauges start={startStr} end={endStr} />
        </div>
        <div className="bg-[#0d2c3d] shadow-lg rounded-lg p-4 col-span-1 row-span-2 row-start-2 col-start-3 flex flex-col">
          <h2 className="text-center text-sm">Cyclone KPIs</h2>
          <CyclonePressureChart start={startStr} end={endStr} isCustom={isCustom} />
        </div>
        <div className="bg-[#0d2c3d] shadow-lg rounded-lg p-4 col-span-1 row-span-2 row-start-2 col-start-4 flex flex-col">
          <h2 className="text-center text-sm">Process Mass Balance</h2>
          <ProcessMassBalanceChart start={startStr} end={endStr} />
        </div>
        <div className="bg-[#0d2c3d] shadow-lg rounded-lg p-4 col-span-2 row-span-1 row-start-3 col-start-1 flex flex-col">
          <h2 className="text-center text-sm">Yield (%)</h2>
          <YieldGauges start={startStr} end={endStr} />
        </div>
        <div className="bg-[#0d2c3d] shadow-lg rounded-lg p-4 col-span-2 row-span-1 row-start-4 col-start-1 flex flex-col">
          <h2 className="text-center text-sm">Builtfontein CM Density Checks</h2>
          <CMDensityChart start={startStr} end={endStr} />
        </div>
        <div className="bg-[#0d2c3d] shadow-lg rounded-lg p-4 col-span-1 row-span-1 row-start-4 col-start-3 flex flex-col">
          <h2 className="text-center text-sm">Weekly Magnetite Consumption (kg/ton)</h2>
          <ConsumptionSummaryChart type="magnetite" start={startStr} end={endStr} isCustom={isCustom} />
        </div>
        <div className="bg-[#0d2c3d] shadow-lg rounded-lg p-4 col-span-1 row-span-1 row-start-4 col-start-4 flex flex-col">
          <h2 className="text-center text-sm">Weekly Flocculant Consumption (g/ton)</h2>
          <ConsumptionSummaryChart type="flocculant" start={startStr} end={endStr} isCustom={isCustom} />
        </div>
      </div>
    </div>
  );
}

export default MetallurgicalAndKPI;
